// Take clean-up in the browser: a recording (or the AI voice's audio) -> trimmed, loudness-normalised 48 kHz mono
// 16-bit WAV, and its length. The server only stores the result; the browser then lip-syncs it (src/audio/lipsync).
// Same steps the server used to run through ffmpeg: highpass at 70 Hz, cut silence at both ends keeping a short
// breath, normalise to -16 LUFS (ITU-R BS.1770) with peaks held under -1.5 dBFS.

// start: seconds cut from the front of the recording (a take filmed on camera moves its face timings by this).
export type TakeResult = { audio: string; duration: number; start: number };
export type CleanTake = { wav: Blob; duration: number; start: number };

export const RATE = 48000;
const BREATH = 0.12, QUIET = 10 ** (-45 / 20), WINDOW = 0.02; // -45 dB over 20 ms windows
const TARGET = -16, CEILING = 10 ** (-1.5 / 20);

export class NoSpeechError extends Error {
  constructor() { super("Didn't catch any speech. Try again a little closer to the mic."); }
}

// Where the sound is: the first and last 20 ms windows louder than -45 dB, widened by the breath. Null if all quiet.
export function soundBounds(s: Float32Array, rate = RATE): { start: number; end: number } | null {
  const w = Math.round(WINDOW * rate);
  const loud = (i: number) => {
    let sum = 0;
    const end = Math.min(s.length, i + w);
    for (let j = i; j < end; j++) sum += s[j]! * s[j]!;
    return Math.sqrt(sum / (end - i)) > QUIET;
  };
  let first = -1;
  for (let i = 0; i + w <= s.length; i += w) if (loud(i)) { first = i; break; }
  if (first < 0) return null;
  let last = first + w;
  for (let i = s.length - w; i > first; i -= w) if (loud(i)) { last = i + w; break; }
  const breath = Math.round(BREATH * rate);
  return { start: Math.max(0, first - breath), end: Math.min(s.length, last + breath) };
}

// One biquad over the samples (a0 already divided out).
function biquad(s: Float32Array, [b0, b1, b2, a1, a2]: number[]) {
  const out = new Float64Array(s.length);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < s.length; i++) {
    const x = s[i]!;
    const y = b0! * x + b1! * x1 + b2! * x2 - a1! * y1 - a2! * y2;
    x2 = x1; x1 = x; y2 = y1; y1 = y;
    out[i] = y;
  }
  return out;
}
const norm = (b: number[], a: number[]) => [b[0]! / a[0]!, b[1]! / a[0]!, b[2]! / a[0]!, a[1]! / a[0]!, a[2]! / a[0]!];

// BS.1770 K-weighting at any rate: a +4 dB shelf above about 1.7 kHz, then a highpass at about 38 Hz. These are the
// standard's 48 kHz filters re-derived for any rate (Brecht De Man's parameters, as in libebur128 and pyloudnorm).
function kWeight(s: Float32Array, rate: number) {
  let K = Math.tan(Math.PI * 1681.974450955533 / rate), Q = 0.7071752369554196;
  const Vh = 10 ** (3.999843853973347 / 20), Vb = Vh ** 0.4996667741545416;
  const shelf = norm([Vh + Vb * K / Q + K * K, 2 * (K * K - Vh), Vh - Vb * K / Q + K * K], [1 + K / Q + K * K, 2 * (K * K - 1), 1 - K / Q + K * K]);
  K = Math.tan(Math.PI * 38.13547087602444 / rate); Q = 0.5003270373238773;
  const hp = norm([1, -2, 1], [1 + K / Q + K * K, 2 * (K * K - 1), 1 - K / Q + K * K]);
  return biquad(Float32Array.from(biquad(s, shelf)), hp);
}

// Integrated loudness in LUFS (mono): 400 ms blocks every 100 ms, gated at -70 LUFS and then 10 LU under the mean.
// A clip shorter than one block is measured whole. -Infinity for silence.
export function loudness(s: Float32Array, rate = RATE): number {
  const k = kWeight(s, rate);
  const block = Math.round(0.4 * rate), step = Math.round(0.1 * rate);
  const z: number[] = [];
  const meanSq = (from: number, to: number) => { let sum = 0; for (let i = from; i < to; i++) sum += k[i]! * k[i]!; return sum / (to - from); };
  if (k.length < block) { if (k.length) z.push(meanSq(0, k.length)); }
  else for (let i = 0; i + block <= k.length; i += step) z.push(meanSq(i, i + block));
  const lufs = (v: number) => -0.691 + 10 * Math.log10(v);
  const avg = (list: number[]) => list.reduce((a, b) => a + b, 0) / list.length;
  const abs = z.filter(v => lufs(v) > -70);
  if (!abs.length) return -Infinity;
  const rel = lufs(avg(abs)) - 10;
  const kept = abs.filter(v => lufs(v) > rel);
  return lufs(avg(kept.length ? kept : abs));
}

// Gain, with peaks held under the ceiling: the gain dips just before a loud peak (5 ms) and recovers over 80 ms.
export function normalise(s: Float32Array, rate = RATE, target = TARGET): Float32Array {
  const l = loudness(s, rate);
  const gain = Number.isFinite(l) ? 10 ** ((target - l) / 20) : 1;
  const g = new Float32Array(s.length);
  for (let i = 0; i < s.length; i++) { const a = Math.abs(s[i]! * gain); g[i] = a > CEILING ? CEILING / a : 1; }
  const release = 1 / (0.08 * rate), attack = 1 / (0.005 * rate);
  for (let i = 1; i < g.length; i++) g[i] = Math.min(g[i]!, g[i - 1]! + release);
  for (let i = g.length - 2; i >= 0; i--) g[i] = Math.min(g[i]!, g[i + 1]! + attack);
  const out = new Float32Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s[i]! * gain * g[i]!;
  return out;
}

// 16-bit PCM WAV, mono.
export function encodeWav(s: Float32Array, rate = RATE): Uint8Array {
  const bytes = new Uint8Array(44 + s.length * 2);
  const v = new DataView(bytes.buffer);
  const text = (at: number, t: string) => { for (let i = 0; i < t.length; i++) v.setUint8(at + i, t.charCodeAt(i)); };
  text(0, "RIFF"); v.setUint32(4, 36 + s.length * 2, true); text(8, "WAVE");
  text(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  text(36, "data"); v.setUint32(40, s.length * 2, true);
  for (let i = 0; i < s.length; i++) {
    const x = Math.max(-1, Math.min(1, s[i]!));
    v.setInt16(44 + i * 2, Math.round(x < 0 ? x * 32768 : x * 32767), true);
  }
  return bytes;
}

// The pure steps after decoding: trim, normalise, encode.
export function cleanSamples(s: Float32Array, rate = RATE): { wav: Uint8Array; duration: number; start: number } {
  const b = soundBounds(s, rate);
  if (!b) throw new NoSpeechError();
  const duration = (b.end - b.start) / rate;
  if (duration < 0.2) throw new NoSpeechError();
  return { wav: encodeWav(normalise(s.subarray(b.start, b.end), rate), rate), duration, start: b.start / rate };
}

// Any audio the browser can decode -> mono 48 kHz, highpassed at 70 Hz (Web Audio does the resampling).
async function decodeHighpassed(data: Blob | ArrayBuffer): Promise<Float32Array> {
  const buf = data instanceof Blob ? await data.arrayBuffer() : data;
  const decoded = await new OfflineAudioContext(1, 1, RATE).decodeAudioData(buf);
  const off = new OfflineAudioContext(1, Math.max(1, Math.ceil(decoded.duration * RATE)), RATE);
  const src = off.createBufferSource();
  src.buffer = decoded;
  const hp = off.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 70;
  hp.Q.value = Math.SQRT1_2;
  src.connect(hp).connect(off.destination);
  src.start();
  return (await off.startRendering()).getChannelData(0);
}

export async function cleanTake(data: Blob | ArrayBuffer): Promise<CleanTake> {
  let samples: Float32Array;
  try { samples = await decodeHighpassed(data); } catch { throw new Error("Couldn't read that audio."); }
  const r = cleanSamples(samples);
  return { wav: new Blob([r.wav as BlobPart], { type: "audio/wav" }), duration: r.duration, start: r.start };
}
